import React, { useMemo, useState } from 'react';
import { Edit3, Eye, EyeOff, Plus, Save, Star } from 'lucide-react';
import { saveAdminReview, setAdminReviewActive } from '../lib/compat.js';
import { AdminFilterBar, AdminModal, EmptyState, Pager, usePagedRows } from './shared.jsx';

export default function AdminReviews({ data, query, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [bookFilter, setBookFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const needle = query.trim().toLowerCase();
  const booksById = useMemo(() => Object.fromEntries((data.books || []).map((book) => [book.id, book])), [data.books]);
  const rows = useMemo(() => {
    return (data.bookRatings || []).filter((item) => {
      const book = booksById[item.bookId];
      const hay = [item.bookTitle, book?.title, item.studentName, item.phone, item.review, item.rating].join(' ').toLowerCase();
      const active = item.active !== false && item.hidden !== true;
      return (!needle || hay.includes(needle))
        && (bookFilter === 'all' || item.bookId === bookFilter)
        && (statusFilter === 'all' || (statusFilter === 'visible' ? active : !active));
    });
  }, [data.bookRatings, booksById, needle, bookFilter, statusFilter]);
  const pager = usePagedRows(rows, 20);

  async function toggle(item) {
    const active = !(item.active !== false && item.hidden !== true);
    await setAdminReviewActive(item.id, item.bookId, active);
    await onRefresh();
  }

  return (
    <>
      <AdminFilterBar>
        <button type="button" className="btn-green" onClick={() => setEditing({ active: true, rating: 5 })}>
          <Plus size={14} /> Add Review
        </button>
        <label>
          Book
          <select value={bookFilter} onChange={(e) => setBookFilter(e.target.value)}>
            <option value="all">All Books</option>
            {(data.books || []).map((book) => <option value={book.id} key={book.id}>{book.title}</option>)}
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Reviews</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
        </label>
      </AdminFilterBar>

      <div className="adminCard">
        <div className="adminCardHead">
          <h3>Book Reviews</h3>
          <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{rows.length} review(s)</span>
        </div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr><th>Book</th><th>Student</th><th>Rating</th><th>Review</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {pager.paged.map((item) => {
                const book = booksById[item.bookId];
                const visible = item.active !== false && item.hidden !== true;
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.bookTitle || book?.title || 'Book'}</strong>
                      <span style={{ color: 'var(--admin-muted)', fontSize: 11 }}>{item.bookId}</span>
                    </td>
                    <td>
                      <strong>{item.studentName || 'Verified Student'}</strong>
                      <span style={{ color: 'var(--admin-muted)', fontSize: 11 }}>{item.phone || item.orderId || 'Manual review'}</span>
                    </td>
                    <td><span className="reviewStars">{'★'.repeat(Number(item.rating || 0))}</span></td>
                    <td style={{ maxWidth: 340, fontSize: 12, color: 'var(--admin-muted)' }}>{item.review || 'No text review'}</td>
                    <td><span className={`badge ${visible ? 'b-active' : 'b-canc'}`}>{visible ? 'Visible' : 'Hidden'}</span></td>
                    <td>
                      <div className="rowActions">
                        <button type="button" onClick={() => setEditing(item)}><Edit3 size={13} /> Edit</button>
                        <button type="button" onClick={() => toggle(item)} style={{ color: visible ? 'var(--admin-muted)' : 'var(--admin-green)' }}>
                          {visible ? <EyeOff size={13} /> : <Eye size={13} />}
                          {visible ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <EmptyState title="No reviews found" body="Add or manage book reviews from here." />}
        </div>
        <Pager state={pager} total={rows.length} />
      </div>

      {editing && (
        <ReviewModal
          review={editing}
          books={data.books || []}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onRefresh(); }}
        />
      )}
    </>
  );
}

function ReviewModal({ review, books, onClose, onSaved }) {
  const selectedBook = books.find((book) => book.id === (review.bookId || ''));
  const [form, setForm] = useState({
    bookId: review.bookId || '',
    bookTitle: review.bookTitle || selectedBook?.title || '',
    studentName: review.studentName || 'Verified Student',
    phone: review.phone || '',
    orderId: review.orderId || '',
    rating: review.rating || 5,
    review: review.review || '',
    active: review.active !== false && review.hidden !== true
  });
  const [msg, setMsg] = useState('');

  function setBook(bookId) {
    const book = books.find((entry) => entry.id === bookId);
    setForm((current) => ({ ...current, bookId, bookTitle: book?.title || '' }));
  }

  async function save() {
    try {
      await saveAdminReview({ id: review.id, ...form });
      await onSaved();
    } catch (e) {
      setMsg(e.message || 'Unable to save review.');
    }
  }

  return (
    <AdminModal title={review.id ? 'Edit Review' : 'Add Review'} onClose={onClose} wide>
      {msg && <div className="banner danger compact">{msg}</div>}
      <div className="adminFormGrid">
        <label className="full">
          Book *
          <select value={form.bookId} onChange={(e) => setBook(e.target.value)} disabled={!!review.id}>
            <option value="">Select book...</option>
            {books.map((book) => <option value={book.id} key={book.id}>{book.title}</option>)}
          </select>
        </label>
        <label>
          Student Name
          <input value={form.studentName} onChange={(e) => setForm({ ...form, studentName: e.target.value })} placeholder="Verified Student" />
        </label>
        <label>
          Mobile / Reference
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional" />
        </label>
        <label>
          Rating *
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}>
            {[5, 4, 3, 2, 1].map((star) => <option value={star} key={star}>{star} Star</option>)}
          </select>
        </label>
        <label>
          Order ID
          <input value={form.orderId} onChange={(e) => setForm({ ...form, orderId: e.target.value })} placeholder="Optional" />
        </label>
        <label className="full">
          Review
          <textarea value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} placeholder="Student review text" rows={4} />
        </label>
      </div>
      <div className="modalActions">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Visible on website
        </label>
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save}><Save size={14} /> Save Review</button>
      </div>
      <div style={{ marginTop: 10, color: 'var(--admin-muted)', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
        <Star size={13} /> Visible reviews are included in public book ratings and schema.
      </div>
    </AdminModal>
  );
}
